'use client';

import { Flex } from '@coolblue-development/freddy';
import { Product as GlobalProduct } from '@features/product/typings';
import useTestId from '@hooks/useTestId';
import {
  filterSeparateColorDiscriminant,
  getProductsWithCurrentColor
} from '@utils/collectionUtils';
import { discriminantValuesWithProducts } from '@utils/collectionUtils/shouldShowCollection';
import { Fragment } from 'react';

import {
  type Collection,
  CollectionType,
  PresentationFormat,
  type ProductType,
  Variant
} from '../Collection.types';
import { CollectionColorPicker, CollectionTitle } from '../Shared';
import { DropdownCollection } from './DropdownCollection';

type DropdownCollectionDesktopProps = {
  collection: Collection;
  productType?: ProductType | null;
  product: GlobalProduct;
};

export const DropdownCollectionDesktop = ({
  collection,
  productType,
  product
}: DropdownCollectionDesktopProps) => {
  const { isShowingPriceDifference } = collection.attributes;
  const productsWithCurrentColor = getProductsWithCurrentColor(collection);
  const testId = useTestId('collection-dropdown-desktop');

  return (
    <>
      <CollectionTitle title={collection.title} productType={productType} variant='small' />
      <Flex gap={400} direction='column' {...testId}>
        {collection.discriminants.map((discriminant, discriminantIndex) => {
          const mappedDiscriminant =
            discriminant.presentationFormat === PresentationFormat.CARDS_WITH_IMAGES
              ? filterSeparateColorDiscriminant(collection, discriminant)
              : discriminant;

          if (discriminantValuesWithProducts(mappedDiscriminant.values).length <= 1) {
            return <Fragment key={discriminantIndex} />;
          }

          return mappedDiscriminant.presentationFormat === PresentationFormat.CARDS_WITH_IMAGES ? (
            <CollectionColorPicker
              key={`color-${discriminantIndex}`}
              discriminant={mappedDiscriminant}
              index={discriminantIndex}
              variant={Variant.DESKTOP}
              collectionType={CollectionType.DROPDOWN}
              colorLabel={mappedDiscriminant.displayName}
            />
          ) : (
            <DropdownCollection
              key={mappedDiscriminant.id}
              discriminant={mappedDiscriminant}
              shouldShowPriceDifference={isShowingPriceDifference}
              productsWithCurrentColor={productsWithCurrentColor}
              product={product}
            />
          );
        })}
      </Flex>
    </>
  );
};
